"use client"

import OrgLayout, { useSidebar } from "@/layouts/OrgLayout"
import { useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Plus, Trash2, Edit, Building2, Search, X, ChevronDown, Users, Phone, Mail, Menu } from "lucide-react"
import { getCSRFToken } from "@/lib/utils"
import { createRefreshChannel } from "@/channels/refresh_channel"
import type { Facility, Email, NumberRecord, Department, User } from "@/types"
import { Link } from "@inertiajs/react"

export interface FacilitiesProps {
  facilities: Facility[]
  organizations: { id: number; name: string }[]
  numbers: NumberRecord[]
  emails: Email[]
  users: User[]
  departments: Department[]
}

function Facilities({ facilities: initialFacilities, numbers, emails, users, departments, organizations }: FacilitiesProps) {
  const [facilities, setFacilities] = useState<Facility[]>(initialFacilities || [])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [organization, setOrganization] = useState<{ id: number; name: string } | null>(null)
  const [editing, setEditing] = useState<Facility | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const [facilityName, setFacilityName] = useState("")
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([])
  const [selectedEmails, setSelectedEmails] = useState<number[]>([])
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([])

  const [searchNumber, setSearchNumber] = useState("")
  const [searchEmail, setSearchEmail] = useState("")
  const [searchUser, setSearchUser] = useState("")
  const [searchDept, setSearchDept] = useState("")

  const [openFacility, setOpenFacility] = useState<number | null>(null)

  useEffect(() => {
    createRefreshChannel()
  }, [])

  // When editing, prepopulate all the assigned ids
  useEffect(() => {
    if (!editing?.id) return

    const facility = facilities.find(f => f.id === editing.id)
    if (!facility) return

    setSelectedNumbers(facility.number_ids || [])
    setSelectedEmails(facility.email_ids || [])
    setSelectedUsers(facility.user_ids || [])
    setSelectedDepartments(facility.department_ids || [])

    setFacilityName(facility.name || "")
    setOrganization(organizations.find(org => org.id === facility.organization_id) || null)
  }, [editing?.id, facilities, organizations])

  const resetForm = () => {
    setEditing(null)
    setFacilityName("")
    setOrganization(null)
    setSelectedNumbers([])
    setSelectedEmails([])
    setSelectedUsers([])
    setSelectedDepartments([])
  }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const csrfToken = getCSRFToken()
    const payload = {
      facility: {
        name: facilityName,
        organization_id: organization?.id,
        number_ids: selectedNumbers,
        email_ids: selectedEmails,
        user_ids: selectedUsers,
        department_ids: selectedDepartments,
      },
    }

    const method = editing ? "PUT" : "POST"
    const url = editing ? `/facility/${editing.id}` : "/facility"

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data.facility) {
          setFacilities(prev =>
            editing ? prev.map(f => (f.id === editing.id ? data.facility : f)) : [...prev, data.facility],
          )
        }
      })
      .finally(() => {
        setDialogOpen(false)
        resetForm()
      })
  }

  const removeFacility = (id: number) => {
    const csrfToken = getCSRFToken()
    fetch(`/facility/${id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken || "" },
    }).then(() => setFacilities(facilities.filter(f => f.id !== id)))
  }

  const toggleSelection = (set: React.Dispatch<React.SetStateAction<number[]>>, arr: number[], id: number) => {
    set(arr.includes(id) ? arr.filter(i => i !== id) : [...arr, id])
  }

  const searchFiltered = (items: any[], field: string, query: string, selected: number[]) =>
    items.filter(i => i[field].toLowerCase().includes(query.toLowerCase()) && !selected.includes(i.id))

  const filteredFacilities = facilities.filter(f => f.name?.toLowerCase().includes(searchQuery.toLowerCase()))

  const { setShowSidebar } = useSidebar()
  return (
    <div className="space-y-6 w-full">
      <div className="flex items-center gap-4 mt-2">
        <Menu className="h-7 w-7 ml-2 md:hidden " onClick={() => setShowSidebar(true)} />
        <Link href="/">
        <h2 className="text-2xl  font-semibold">Facilities</h2>
          </Link>
      </div>

      <div className="flex flex-col md:flex-row  gap-4">
        <div className="flex items-center gap-2 w-full">
          <Search className="h-4 w-4 hidden md:block text-muted-foreground" />
          <Input
            placeholder="Search facilities..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="ml-2"
          />
        </div>

        <Dialog
          open={dialogOpen}
          onOpenChange={open => {
            setDialogOpen(open)
            if (!open) resetForm()
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="">
              <Plus className="h-4 w-4 mr-2" />
              Add Facility
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Facility" : "Add Facility"}</DialogTitle>
                <DialogDescription>Set associated resources for this facility.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={facilityName} onChange={e => setFacilityName(e.target.value)} required />
                </div>

                {[
                  {
                    label: "Numbers",
                    icon: Phone,
                    list: numbers,
                    query: searchNumber,
                    setQuery: setSearchNumber,
                    selected: selectedNumbers,
                    setSelected: setSelectedNumbers,
                    field: "number",
                  },
                  {
                    label: "Emails",
                    icon: Mail,
                    list: emails,
                    query: searchEmail,
                    setQuery: setSearchEmail,
                    selected: selectedEmails,
                    setSelected: setSelectedEmails,
                    field: "address",
                  },
                  {
                    label: "Departments",
                    icon: Building2,
                    list: departments,
                    query: searchDept,
                    setQuery: setSearchDept,
                    selected: selectedDepartments,
                    setSelected: setSelectedDepartments,
                    field: "name",
                  },
                  {
                    label: "Users",
                    icon: Users,
                    list: users,
                    query: searchUser,
                    setQuery: setSearchUser,
                    selected: selectedUsers,
                    setSelected: setSelectedUsers,
                    field: "email",
                  },
                ].map(({ label, icon: Icon, list, query, setQuery, selected, setSelected, field }) => (
                  <div key={label} className="space-y-2">
                    <Label>{label}</Label>
                    {selected.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
                        {selected.map(id => {
                          const item = list.find(i => i.id === id)
                          return (
                            item && (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {item[field]}
                                <button
                                  type="button"
                                  onClick={() => toggleSelection(setSelected, selected, id)}
                                  className="ml-1 hover:bg-destructive/20 rounded-full"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            )
                          )
                        })}
                      </div>
                    )}
                    <div className="relative">
                      <Input
                        placeholder={`Search ${label.toLowerCase()}...`}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                      />
                      {query && searchFiltered(list, field, query, selected).length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                          {searchFiltered(list, field, query, selected).map(item => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => toggleSelection(setSelected, selected, item.id)}
                              className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                            >
                              {item[field]}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="submit">{editing ? "Update" : "Create Facility"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {filteredFacilities.map(facility => (
          <Collapsible
            key={facility.id}
            open={openFacility === facility.id}
            onOpenChange={() => setOpenFacility(openFacility === facility.id ? null : facility.id)}
          >
            <Card className="p-4">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Building2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="font-medium truncate">{facility.name}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${openFacility === facility.id ? "rotate-180" : ""}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={e => {
                        e.stopPropagation()
                        setEditing(facility)
                        setDialogOpen(true)
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={e => {
                        e.stopPropagation()
                        removeFacility(facility.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>


                <div className="pl-8 pt-2 space-y-2 text-sm">
                  {[
                    { label: "Numbers", ids: facility.number_ids, list: numbers, field: "number" },
                    { label: "Emails", ids: facility.email_ids, list: emails, field: "address" },
                    { label: "Departments", ids: facility.department_ids, list: departments, field: "name" },
                    { label: "Users", ids: facility.user_ids, list: users, field: "email" },
                  ].map(({ label, ids, list, field }) => {
                    if (!ids || ids.length === 0) return null

                    const resolved = ids
                      .map(id => list?.find(i => i.id === id))
                      .filter(Boolean) // remove undefined

                    if (resolved.length === 0) return null

                    return (
                      <div key={label} className="flex py-2 items-center flex-wrap gap-1 border-t pt-1">
                        <span className="font-bold mr-2">{label}:</span>
                        {resolved.map((item: any) => (
                          <Badge key={item.id} variant="outline" className="text-xs">
                            {item[field]}
                          </Badge>
                        ))}
                      </div>
                    )
                  })}
                </div>

              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>
    </div>
  )
}

Facilities.layout = (page: React.ReactNode) => <OrgLayout activeSection="facilities">{page}</OrgLayout>

export default Facilities

